import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { updateUser, User } from '../../api/admin';

interface UserEditDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh?: boolean) => void;
}

const UserEditDialog: React.FC<UserEditDialogProps> = ({ open, user, onClose }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'user' | 'admin' | 'b2b-user' | 'mobile-users'>('user');
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setRole(user.role);
      setStatus(user.status);
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      await updateUser(user.username, { email, role, status });
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.actions.edit')} - {user?.username}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label={t('admin.users.columns.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          select
          label={t('admin.users.columns.role')}
          value={role}
          onChange={(e) => setRole(e.target.value as any)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="user">user</MenuItem>
          <MenuItem value="admin">admin</MenuItem>
          <MenuItem value="b2b-user">b2b-user</MenuItem>
          <MenuItem value="mobile-users">mobile-users</MenuItem>
        </TextField>
        <TextField
          select
          label={t('admin.users.columns.status')}
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="active">active</MenuItem>
          <MenuItem value="disabled">disabled</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;
